/**
 * Airwallex Apple Pay Adapter
 *
 * Uses native ApplePaySession API directly (not Stripe.js) to show the payment sheet
 * and extract the encrypted payment token for Airwallex processing.
 *
 * Flow:
 * 1. initialize() - Checks if ApplePaySession is available
 * 2. canMakePayment() - Checks if user can pay with Apple Pay
 * 3. showPaymentSheet() - Shows Apple Pay sheet with merchant validation callback
 *
 * The key difference from Stripe's approach is that we use the native
 * ApplePaySession API and handle merchant validation via Airwallex's
 * payment_session/start API which validates with Apple.
 *
 * Mock scenarios are supported for E2E testing without real Apple Pay.
 */

/// <reference types="@types/applepayjs" />

export enum AirwallexApplePayMockScenario {
  None = "none",
  Success = "success",
  Cancelled = "cancelled",
}

// Apple Pay token structure from payment.token
export interface ApplePayEncryptedToken {
  paymentData: {
    version?: string;
    data: string;
    signature: string;
    header: {
      ephemeralPublicKey: string;
      publicKeyHash: string;
      transactionId: string;
    };
  };
  paymentMethod: {
    displayName: string;
    network: string;
    type: string;
  };
  transactionIdentifier: string;
}

export interface AirwallexApplePayConfig {
  amount: number; // Amount in atomic units (cents)
  currency: string; // e.g., "USD"
  country: string; // e.g., "US"
  merchantName?: string; // Display name on Apple Pay sheet
}

export type AirwallexApplePayShowResult =
  | {
      success: true;
      token: ApplePayEncryptedToken;
      payerEmail?: string;
      payerFirstName?: string;
      payerLastName?: string;
      complete: (status: "success" | "fail") => void;
    }
  | { success: false; cancelled: true }
  | { success: false; error: string };

// Merchant validation callback type - called during onvalidatemerchant
export type MerchantValidationCallback = (
  validationUrl: string,
) => Promise<{ merchantSession?: object; error?: string }>;

export class AirwallexApplePayAdapter {
  private config: AirwallexApplePayConfig | null = null;
  private mockScenario: AirwallexApplePayMockScenario;

  constructor(mockScenario?: AirwallexApplePayMockScenario) {
    this.mockScenario = mockScenario ?? AirwallexApplePayMockScenario.None;
  }

  /**
   * Initialize the adapter with payment configuration.
   * Returns true if Apple Pay is available on this device.
   */
  initialize(config: AirwallexApplePayConfig): boolean {
    // Mock scenarios bypass real Apple Pay initialization
    if (this.mockScenario !== AirwallexApplePayMockScenario.None) {
      console.log("[MockApplePay:Airwallex] initialize called (mock mode)");
      this.config = config;
      return true;
    }

    // Check if ApplePaySession is available (Safari on iOS/macOS only)
    if (typeof window === "undefined" || !window.ApplePaySession) {
      console.error("[ApplePay:Airwallex] ApplePaySession not available (not Safari or unsupported device)");
      return false;
    }

    this.config = config;
    return true;
  }

  /**
   * Check if the user can make a payment with Apple Pay.
   * Returns true if Apple Pay is set up on this device.
   */
  async canMakePayment(): Promise<boolean> {
    // Mock scenarios always return true
    if (this.mockScenario !== AirwallexApplePayMockScenario.None) {
      console.log("[MockApplePay:Airwallex] canMakePayment: true (mock mode)");
      return true;
    }

    if (!window.ApplePaySession) {
      return false;
    }

    try {
      const canMake = window.ApplePaySession.canMakePayments();
      console.log("[ApplePay:Airwallex] canMakePayments:", canMake);
      return canMake;
    } catch (error) {
      console.error("[ApplePay:Airwallex] canMakePayment error:", error);
      return false;
    }
  }

  /**
   * Convert atomic units (cents) to display format for Apple Pay.
   * Apple Pay expects a string like "10.00" for $10.00.
   */
  private formatAmount(amountAtom: number, currency: string): string {
    // Most currencies use 2 decimal places, some like JPY use 0
    const zeroDecimalCurrencies = [
      "BIF",
      "CLP",
      "DJF",
      "GNF",
      "ISK",
      "JPY",
      "KMF",
      "KRW",
      "PYG",
      "RWF",
      "UGX",
      "VND",
      "VUV",
      "XAF",
      "XOF",
      "XPF",
    ];
    const isZeroDecimal = zeroDecimalCurrencies.includes(currency.toUpperCase());

    if (isZeroDecimal) {
      return amountAtom.toString();
    }

    return (amountAtom / 100).toFixed(2);
  }

  /**
   * Show the Apple Pay payment sheet and return the encrypted token.
   *
   * This uses the native ApplePaySession API with a two-step flow:
   * 1. onvalidatemerchant - Called by Apple to validate the merchant
   *    The callback should call our backend which calls Airwallex's payment_session/start
   * 2. onpaymentauthorized - Called when user authorizes with Face ID/Touch ID
   *    Returns the encrypted token for processing
   *
   * @param onMerchantValidation - Callback to validate merchant with backend
   * @returns Promise with token or error
   */
  async showPaymentSheet(onMerchantValidation: MerchantValidationCallback): Promise<AirwallexApplePayShowResult> {
    // Handle mock scenarios
    if (this.mockScenario === AirwallexApplePayMockScenario.Success) {
      console.log("[MockApplePay:Airwallex] showPaymentSheet: returning mock success token");
      const mockToken: ApplePayEncryptedToken = {
        paymentData: {
          version: "EC_v1",
          data: "MOCK_ENCRYPTED_DATA",
          signature: "MOCK_SIGNATURE",
          header: {
            ephemeralPublicKey: "MOCK_PUBLIC_KEY",
            publicKeyHash: "MOCK_HASH",
            transactionId: "mock_txn_123",
          },
        },
        paymentMethod: {
          displayName: "Visa 4242",
          network: "Visa",
          type: "debit",
        },
        transactionIdentifier: "MOCK_TXN_ID",
      };
      return {
        success: true,
        token: mockToken,
        payerEmail: "mock@example.com",
        payerFirstName: "Mock",
        payerLastName: "User",
        complete: (status) => console.log(`[MockApplePay:Airwallex] complete: ${status}`),
      };
    }

    if (this.mockScenario === AirwallexApplePayMockScenario.Cancelled) {
      console.log("[MockApplePay:Airwallex] showPaymentSheet: returning mock cancelled");
      return { success: false, cancelled: true };
    }

    // Real Apple Pay flow
    if (!this.config) {
      return { success: false, error: "Apple Pay not initialized" };
    }

    if (!window.ApplePaySession) {
      return { success: false, error: "ApplePaySession not available" };
    }

    const config = this.config;
    const amount = this.formatAmount(config.amount, config.currency);

    // Create the Apple Pay payment request
    const paymentRequest: ApplePayJS.ApplePayPaymentRequest = {
      countryCode: config.country.toUpperCase(),
      currencyCode: config.currency.toUpperCase(),
      supportedNetworks: ["visa", "masterCard", "amex", "discover"],
      merchantCapabilities: ["supports3DS"],
      total: {
        label: config.merchantName || "Total",
        amount: amount,
        type: "final",
      },
      // Surface the payer's email and name so the backend can fill missing customer info.
      requiredBillingContactFields: ["email", "name"],
    };

    return new Promise((resolve) => {
      try {
        // Create ApplePaySession (version 3 is widely supported)
        // biome-ignore lint/style/noNonNullAssertion: ApplePaySession is guaranteed to exist (checked in canMakePayments)
        const session = new window.ApplePaySession!(3, paymentRequest);

        // Capture status constants at creation time (before optional chaining could yield undefined)
        // biome-ignore lint/style/noNonNullAssertion: ApplePaySession existence verified above
        const STATUS_SUCCESS = window.ApplePaySession!.STATUS_SUCCESS;
        // biome-ignore lint/style/noNonNullAssertion: ApplePaySession existence verified above
        const STATUS_FAILURE = window.ApplePaySession!.STATUS_FAILURE;

        // Handle merchant validation
        session.onvalidatemerchant = async (event: ApplePayJS.ApplePayValidateMerchantEvent) => {
          console.log("[ApplePay:Airwallex] onvalidatemerchant called", event.validationURL);

          try {
            const validationResult = await onMerchantValidation(event.validationURL);

            if (validationResult.error || !validationResult.merchantSession) {
              console.error("[ApplePay:Airwallex] Merchant validation failed:", validationResult.error);
              session.abort();
              resolve({
                success: false,
                error: validationResult.error || "Merchant validation failed",
              });
              return;
            }

            // Complete merchant validation with the session from Apple
            session.completeMerchantValidation(validationResult.merchantSession);
            console.log("[ApplePay:Airwallex] Merchant validation completed");
          } catch (error) {
            console.error("[ApplePay:Airwallex] Merchant validation error:", error);
            session.abort();
            resolve({
              success: false,
              error: `Merchant validation error: ${error}`,
            });
          }
        };

        // Handle payment authorization (user approved with Face ID/Touch ID)
        session.onpaymentauthorized = (event: ApplePayJS.ApplePayPaymentAuthorizedEvent) => {
          console.log("[ApplePay:Airwallex] onpaymentauthorized called");

          // Extract the encrypted token
          const payment = event.payment;
          const token: ApplePayEncryptedToken = {
            paymentData: payment.token.paymentData as ApplePayEncryptedToken["paymentData"],
            paymentMethod: {
              displayName: payment.token.paymentMethod.displayName,
              network: payment.token.paymentMethod.network,
              type: payment.token.paymentMethod.type,
            },
            transactionIdentifier: payment.token.transactionIdentifier,
          };

          const payerEmail = payment.billingContact?.emailAddress ?? payment.shippingContact?.emailAddress ?? undefined;
          const payerFirstName = payment.billingContact?.givenName ?? payment.shippingContact?.givenName ?? undefined;
          const payerLastName = payment.billingContact?.familyName ?? payment.shippingContact?.familyName ?? undefined;

          // Create the complete callback that the caller will use after confirming
          const complete = (status: "success" | "fail") => {
            console.log("[ApplePay:Airwallex] completing session with status:", status);
            const appleStatus = status === "success" ? STATUS_SUCCESS : STATUS_FAILURE;
            session.completePayment({ status: appleStatus });
          };

          resolve({
            success: true,
            token,
            payerEmail,
            payerFirstName,
            payerLastName,
            complete,
          });
        };

        // Handle cancellation
        session.oncancel = () => {
          console.log("[ApplePay:Airwallex] oncancel - user cancelled");
          resolve({ success: false, cancelled: true });
        };

        // Start the session - this shows the Apple Pay sheet
        console.log("[ApplePay:Airwallex] Starting Apple Pay session");
        session.begin();
      } catch (error) {
        console.error("[ApplePay:Airwallex] Session creation error:", error);
        resolve({
          success: false,
          error: `Failed to create Apple Pay session: ${error}`,
        });
      }
    });
  }
}
